// client/src/lib/AppContext.tsx
import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { supabase } from './supabase';
import { Dispatch, SetStateAction } from 'react';

// Define User type to match useAuth schema
export interface User {
  id: string;
  name: string;
  email: string;
  role: 'client' | 'agent';
  phone?: string | null;
  avatar?: string | null;
  avatar_url?: string | null;
  bio?: string | null;
  city?: string | null;
  fcmToken?: string | null;
  notificationPreferences?: {
    transactionUpdates: boolean;
    appointments: boolean;
    messages: boolean;
  } | null | undefined;
}

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001';

export type TabType = 'home' | 'favorites' | 'schedule' | 'messages';

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  register: (
    name: string,
    email: string,
    password: string,
    role: 'client' | 'agent'
  ) => Promise<User | null>;
  login: (email: string, password: string) => Promise<User | null>;
  logout: () => Promise<void>;
  resetPassword: (email: string, newPassword: string, token: string) => Promise<boolean>;
  clearError: () => void;
  setUser: Dispatch<SetStateAction<User | null>>;
}

interface AppContextType {
  auth: AuthState;
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
  isAuthenticated: boolean;
  isGuest: boolean;
  startGuestSession: () => void;
  endGuestSession: () => void;
  openAuthPrompt: (message?: string) => void;
  closeAuthPrompt: () => void;
  showAuthPrompt: boolean;
  authPromptMessage: string;
  registerFCMToken: (token: string) => Promise<void>;
  currentUser: User | null;
  favorites: string[];
  toggleFavorite: (id: string, searchType?: 'sale' | 'rent' | 'sold') => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppProvider({
  children,
  value,
}: {
  children: ReactNode;
  value: Omit<AppContextType, 'favorites' | 'toggleFavorite'>;
}) {
  const [error, setError] = useState<string | null>(null);
  const [favorites, setFavorites] = useState<string[]>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('favorites');
      return stored ? JSON.parse(stored) : [];
    }
    return [];
  });

  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [authPromptMessage, setAuthPromptMessage] = useState('Create an account or sign in to save favorites.');
  const openAuthPrompt = (message?: string) => {
    if (message) setAuthPromptMessage(message);
    setShowAuthPrompt(true);
  };
  const closeAuthPrompt = () => {
    setShowAuthPrompt(false);
    setAuthPromptMessage('Create an account or sign in to save favorites.');
  };

  useEffect(() => {
    localStorage.setItem('favorites', JSON.stringify(favorites));
  }, [favorites]);

  // Load favorites from backend when user is available
  useEffect(() => {
    let isMounted = true;
    async function loadFavoritesFromServer() {
      try {
        const { data } = await supabase.auth.getSession();
        const token = data?.session?.access_token;
        if (!token || !value.auth.user) return;
        
        // Get the API base URL for Fly.io
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://my-agent-api.fly.dev';
        
        const res = await fetch(`${apiBaseUrl}/api/favorites`, {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        });
        if (!res.ok) return;
        const json = await res.json();
        if (!isMounted) return;
        const serverFavorites: string[] = Array.isArray(json?.favorites) ? json.favorites : [];
        setFavorites(serverFavorites);
      } catch (err) {
        console.error('Error loading favorites:', err);
      }
    }
    loadFavoritesFromServer();
    return () => {
      isMounted = false;
    };
  }, [value.auth.user]);

  const toggleFavorite = async (id: string, searchType?: 'sale' | 'rent' | 'sold') => {
    // Optimistic update
    if (value.isGuest || !value.auth.user) {
      setShowAuthPrompt(true);
      return;
    }
    setFavorites((prev) => (prev.includes(id) ? prev.filter((fid) => fid !== id) : [...prev, id]));
    try {
      const { data } = await supabase.auth.getSession();
      const token = data?.session?.access_token;
      if (!token || !value.auth.user) {
        return;
      }
      
      // Get the API base URL for Fly.io
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://my-agent-api.fly.dev';
      
      const isAdding = !favorites.includes(id);
      if (isAdding) {
        const response = await fetch(`${apiBaseUrl}/api/favorites`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          credentials: 'include',
          body: JSON.stringify({ propertyId: id, searchType }),
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          console.error('Failed to add favorite:', errorData);
          throw new Error(errorData.message || 'Failed to add favorite');
        }
      } else {
        const response = await fetch(`${apiBaseUrl}/api/favorites/${encodeURIComponent(id)}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          console.error('Failed to remove favorite:', errorData);
          throw new Error(errorData.message || 'Failed to remove favorite');
        }
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
      // Revert on failure
      setFavorites((prev) => (prev.includes(id) ? prev.filter((fid) => fid !== id) : [...prev, id]));
    }
  };


  const clearError = () => setError(null);

  const auth = {
    ...value.auth,
    user: value.auth.user,
    error: value.auth.error || error,
    clearError,
    setUser: value.auth.setUser,
  };

  return (
    <AppContext.Provider
      value={{
        ...value,
        auth,
        favorites,
        toggleFavorite,
        openAuthPrompt,
        closeAuthPrompt,
        showAuthPrompt,
        authPromptMessage,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useAppContext() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
}